import React from 'react';
import { Table, Button, Tooltip } from '../../../components';
import { useSipProvidersManagement } from './hooks/useSipProvidersManagement';
import { SipProviderModals, SipProviderActions } from './components';
import { SIP_PROVIDER_LABELS } from './constants';
import type { ExternalSipProviderDto } from '../../../api/sip-providers';

export const SipProvidersManagementTab: React.FC = () => {
  const {
    providers,
    loading,
    formValues,
    setFormValues,
    isOpened,
    onOpen,
    onClose,
    isOpenedEditModal,
    onCloseEditModal,
    handleCreateProviderSubmit,
    handleEditProviderSubmit,
    handleEditClick,
    creatingProvider,
    updatingProvider,
  } = useSipProvidersManagement();

  return (
    <div className="p-4">
      <div className="flex flex-wrap justify-between items-end mb-4 gap-y-md">
        <div />

        <div className="flex gap-x-sm">
          <Button
            label={SIP_PROVIDER_LABELS.CREATE_PROVIDER}
            onClick={onOpen}
            size="medium"
            disabled={creatingProvider}
          />
        </div>
      </div>

      <SipProviderModals
        isOpened={isOpened}
        onClose={onClose}
        isOpenedEditModal={isOpenedEditModal}
        onCloseEditModal={onCloseEditModal}
        formValues={formValues}
        setFormValues={setFormValues}
        handleCreateProviderSubmit={handleCreateProviderSubmit}
        handleEditProviderSubmit={handleEditProviderSubmit}
        creatingProvider={creatingProvider}
        updatingProvider={updatingProvider}
      />

      <div className="overflow-x-auto">
        <Table
          data={providers}
          loading={loading}
          emptyMessage={SIP_PROVIDER_LABELS.NO_PROVIDERS}
          config={{
            columns: [
              {
                header: SIP_PROVIDER_LABELS.PROVIDER_NAME,
                cellClassName: 'min-w-[15rem] text-left',
                getValue: ({ name }: ExternalSipProviderDto) => (
                  <div className="truncate font-medium">
                    {name || 'Unnamed'}
                  </div>
                ),
              },
              {
                header: SIP_PROVIDER_LABELS.DESCRIPTION,
                cellClassName: 'min-w-[15rem] max-w-[25rem] text-left',
                getValue: ({ description }: ExternalSipProviderDto) => {
                  const displayText = description || '-';
                  const shouldShowTooltip =
                    description && description.length > 50;

                  if (shouldShowTooltip) {
                    return (
                      <div className="relative w-full">
                        <Tooltip
                          content={description}
                          position="top"
                          className="absolute"
                          iconClassName="opacity-0"
                          trigger="hover"
                        >
                          <div className="truncate text-gray-600">
                            {displayText}
                          </div>
                        </Tooltip>
                      </div>
                    );
                  }

                  return (
                    <div className="truncate text-gray-600">{displayText}</div>
                  );
                },
              },
              {
                header: SIP_PROVIDER_LABELS.ACTIONS,
                cellClassName: 'min-w-[6rem]',
                align: 'center',
                getValue: (provider: ExternalSipProviderDto) => (
                  <SipProviderActions
                    onEdit={() => handleEditClick(provider)}
                  />
                ),
              },
            ],
          }}
        />
      </div>
    </div>
  );
};
